import type { AccountSearchResult } from "@wallyo/contracts/console/accounts";
import { useRef, useState } from "react";
import { getCustomerAccount, searchAccounts } from "../../api/console-api";
import { useApi } from "../../api/context";
import { featureName, t } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { describeKind, nameOf } from "../customers/customer-format";
import { queryProblem } from "../customers/search-state";
import type { PickedPerson } from "./features-model";

type Search =
  | { status: "idle" }
  | { status: "searching" }
  | { status: "done"; results: readonly AccountSearchResult[] }
  | { status: "failed"; error: unknown };

/**
 * Finds the person (or pro) an exception is for, through GET /console/accounts
 * (SEARCH_ACCOUNTS). A pro profile's id is on the account page, so picking a
 * pro reads GET /console/accounts/:userId (VIEW_ACCOUNTS) before it counts.
 */
export function PersonPicker({
  pro,
  canView,
  picked,
  onPick,
}: {
  /** Picking a pro profile (`proProfile`) rather than a person (`user`). */
  pro: boolean;
  canView: boolean;
  picked: PickedPerson | null;
  onPick: (person: PickedPerson | null) => void;
}) {
  const { client } = useApi();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState<Search>({ status: "idle" });
  const [problem, setProblem] = useState<string | null>(null);
  const [resolving, setResolving] = useState<{ userId: string; error: unknown } | null>(null);
  const generation = useRef(0);

  const run = () => {
    const trimmed = query.trim();
    const tooShort = queryProblem(trimmed);
    setProblem(tooShort);
    if (tooShort) return;
    const mine = ++generation.current;
    setSearch({ status: "searching" });
    searchAccounts(client, { query: trimmed }).then(
      (page) => generation.current === mine && setSearch({ status: "done", results: page.results }),
      (error: unknown) => generation.current === mine && setSearch({ status: "failed", error }),
    );
  };

  const pick = (result: AccountSearchResult) => {
    if (!pro) {
      onPick({ userId: result.userId, name: result.name, proProfileId: null });
      return;
    }
    const mine = ++generation.current;
    setResolving({ userId: result.userId, error: null });
    getCustomerAccount(client, result.userId).then(
      ({ account }) => {
        if (generation.current !== mine) return;
        setResolving(null);
        onPick({ userId: result.userId, name: result.name, proProfileId: account.proProfile?.proProfileId ?? null });
      },
      (error: unknown) => generation.current === mine && setResolving({ userId: result.userId, error }),
    );
  };

  if (picked) {
    return (
      <div className="picked" role="status">
        <span className="meta">{t(pro ? "features.form.pro" : "features.form.person")}</span>
        <b>
          <bdi>{nameOf(picked)}</bdi>
        </b>
        <button className="btn quiet small" type="button" onClick={() => onPick(null)}>
          {t("features.form.pickedChange")}
        </button>
      </div>
    );
  }

  const results =
    search.status === "done" ? search.results.filter((result) => !result.deleted && (!pro || result.proProfile)) : [];
  return (
    <div className="person-picker">
      {/* Inside the exception form: a search row, not a form of its own (forms don't nest). */}
      <div className="search-row" role="search">
        <label className="visually-hidden" htmlFor="exception-person">
          {t(pro ? "features.form.pro" : "features.form.person")}
        </label>
        <input
          id="exception-person"
          value={query}
          placeholder={t("features.form.searchPlaceholder")}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            run();
          }}
        />
        <button className="btn" type="button" onClick={run} disabled={search.status === "searching"}>
          {search.status === "searching" ? t("features.form.searching") : t("features.form.search")}
        </button>
      </div>
      {problem ? <p className="field-problem">{problem}</p> : null}
      {pro && !canView ? (
        <p className="foot">
          {t("features.form.needsView")} <b>{featureName("VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT")}</b>
        </p>
      ) : null}
      {search.status === "failed" ? <ErrorNotice error={search.error} /> : null}
      {resolving?.error ? <ErrorNotice error={resolving.error} /> : null}
      {search.status === "done" && results.length === 0 ? <p className="foot">{t("features.form.noResults")}</p> : null}
      {results.length > 0 ? (
        <ul className="pick-list">
          {results.map((result) => (
            <li key={result.userId}>
              <button
                className="btn quiet small"
                type="button"
                disabled={(pro && !canView) || resolving?.userId === result.userId}
                onClick={() => pick(result)}
              >
                <bdi>{nameOf(result)}</bdi>
              </button>
              <span className="meta">
                {describeKind(result)}
                {result.maskedPhone ? (
                  <>
                    {" "}
                    · <bdi dir="ltr">{result.maskedPhone}</bdi>
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
